import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChange,
    ViewChild,
    ElementRef,
    OnDestroy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { SeTextEditorComponent } from '../se-text-editor/se-text-editor.component';
import { ContentChangedEvent } from '../se-text-editor/prosemirror/event/text-changed-event';
import { SuggestionDataSource } from './suggestion-datasource/suggestion-data-source';
import { ShortcutEvent } from '../se-text-editor/prosemirror/event/shortcut-event';
import { SuggestionFilter, allMatchingFilter, noMatchingFilter } from './suggestion-datasource/suggestion-filter';
import { MenuItem } from '../se-text-editor/menu/menu-item';
import { MenuEvent } from '../se-text-editor/prosemirror/event/menu-event';
import { MenuConfiguration, predefinedMenuConfiguration } from '../se-text-editor/menu/menu-configuration';
import { MenuEnum } from '../se-text-editor/menu/menu-items.enum';
import { CpControlSuggestions, ExtendedSuggestion, SaveExtendedSuggestionEvent, Suggestion } from './suggestion-datasource/suggestion';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import {
    SeSuggestionSelectorOverlayService,
    SeSuggestionSelectorOverlayConfig,
} from '../se-suggestion-selector-overlay/overlay/suggestion-selector-overlay.service';
import { SeSuggestionSelectorOverlayRef } from '../se-suggestion-selector-overlay/overlay/suggestion-selector-overlay-ref';
import { Subscription, Subject, ReplaySubject } from 'rxjs';
import { TextFormats } from '../se-text-editor/menu/text-formats';
import { ShortcutsConfiguration, defaultShortcutList } from './shortcuts';
import { SelectEvent } from '../se-text-editor/prosemirror/event/select-event';
import { ListFormats } from '../se-text-editor/menu/menu-list';

@Component({
    selector: 'se-smart-text-editor',
    templateUrl: './se-smart-text-editor.component.html',
    styleUrls: ['./se-smart-text-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SeSmartTextEditorComponent,
            multi: true,
        },
        SeSuggestionSelectorOverlayService,
    ],
    standalone: false
})
export class SeSmartTextEditorComponent implements OnInit, OnChanges, ControlValueAccessor, OnDestroy {
    @Input() suggestionList?: Suggestion[] = [];
    @Input() debounceTime = 1000; // 1 second
    @Input() autoCompleteEnabled = true;
    @Input() customCommandConfiguration?: MenuItem[];
    @Input() autoCompleteTriggers: string[] = [',', ';', '.', ':', '?', '!'];
    @Input() predefinedMenuConfiguration?: Map<MenuEnum, MenuConfiguration>;
    @Input() language = 'it'; // it,he
    @Input() expandSuggestionButton = false;
    @Input() cpTextEditorSuggestionText?: Map<string, CpControlSuggestions> = new Map<string, CpControlSuggestions>();

    /* summarize in a single object ? */
    @Input() defaultFontSize = '14px';
    @Input() defaultFontFamily = 'arial';
    @Input() defaultTextAlign = 'left';
    @Input() defaultFontWeight = '';
    @Input() defaultFontStyle = 'normal';
    @Input() defaultTextDecoration = 'none';
    @Input() customShortcutConfiguration?: ShortcutsConfiguration[];
    @Input() title?: string;
    @Input() visibilityButtonEnabled = false;
    @Input() visible?: boolean | null = true;
    @Input() isInError? = false;
    @Input() lightLayout = true; // per pagina ECG = true
    @Input() defaultTextName?: string = 'text';
    @Input() isDisabled = false;
    @Input() isFromSpeechModal? = false;

    @Output() contentChanged = new EventEmitter<ContentChangedEvent>();
    @Output() commandRequested = new EventEmitter<MenuEvent>();
    @Output() visibilityNotify = new EventEmitter<boolean>();
    @Output() isEmpty = new EventEmitter<boolean>();
    @Output() startSpeechReconition = new EventEmitter<Array<string>>();
    @Output() selectedSuggestion = new EventEmitter<ExtendedSuggestion>();
    @Output() updateSuggestion = new EventEmitter<SaveExtendedSuggestionEvent>();

    @ViewChild(SeTextEditorComponent, { static: true }) private textEditorComponent!: SeTextEditorComponent;
 
    public value;
    public suggestionDataSource: SuggestionDataSource;
    private suggestionSelectorOverlayConfig: SeSuggestionSelectorOverlayConfig;
    private preSelectedSuggestion: Suggestion;
    private suggestionSelectorOverlayRef: SeSuggestionSelectorOverlayRef;
    private selectedSuggestionSub: Subscription;
    private preSelectedSuggestionSub: Subscription;
    private updateSuggestionsSub: Subscription;
    private overlayStatusEmitter: Subscription;
    private showSuggestions = false;

    private filteredText = '';
    private unsubscribe$ = new Subject<void>();
    private dsSuggestionStream = new Subject<Suggestion[]>();
    private filterStream = new ReplaySubject<SuggestionFilter>(1);
    private textsGroupStream = new Subject<Map<string, CpControlSuggestions>>();
    public shortcutList = defaultShortcutList;

    constructor(
        private suggestionSelectorOverlayComponentService: SeSuggestionSelectorOverlayService,
        private textEditorElementRef: ElementRef,
    ) {}

    ngOnInit() {
        this.initShortcuts();
        this.initSuggestionDataSource();
        this.initMenuToolbar();
    }

    private initShortcuts() {
        if (this.customShortcutConfiguration) {
            this.customShortcutConfiguration.forEach((conf) => {
                if (this.shortcutList.hasOwnProperty(conf.key)) {
                    this.shortcutList[conf.key] = conf.value;
                }
            });
        }
    }

    private initSuggestionDataSource() {
        this.suggestionDataSource = new SuggestionDataSource(this.dsSuggestionStream, this.filterStream, this.textsGroupStream);
        this.dsSuggestionStream.next(this.suggestionList);
        if (this.cpTextEditorSuggestionText) {
            this.textsGroupStream.next(this.cpTextEditorSuggestionText);
        }
        /* inizialmente non ho alcun filtro da applicare e non voglio matching */
        this.suggestionDataSource.$filteredSuggestions
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (newFilteredSuggestionList) => {
                    /* se ci sono suggerimenti mostro altrimenti no */
                    if (newFilteredSuggestionList && newFilteredSuggestionList.length > 0 && this.showSuggestions) {
                         this.showSuggestionSelector();
                    } else {
                        /* se non c'è niente da mostrare chiudo il selector */
                        if (this.suggestionSelectorOverlayRef) {
                            this.preSelectedSuggestionSub.unsubscribe();
                            this.selectedSuggestionSub.unsubscribe();
                            this.suggestionSelectorOverlayRef.close();
                            this.overlayStatusEmitter.unsubscribe();
                            this.updateSuggestionsSub.unsubscribe();
                        }
                    }
                },
            });
        this.suggestionDataSource.$textsGroup.pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (newTextsGroup) => {
                    if (newTextsGroup.size > 0) {
                        console.log('newTextsGroup', newTextsGroup);
                    }
                }
            })
    }

    private initMenuToolbar() {
        if (!this.predefinedMenuConfiguration) {
            this.predefinedMenuConfiguration = predefinedMenuConfiguration;
        }
    }

    private showSuggestionSelector() {
        if (this.suggestionSelectorOverlayRef && !this.suggestionSelectorOverlayRef.isClosed) {
            return;
        }

        this.suggestionSelectorOverlayConfig = {
            connectedComponentRef: this.textEditorElementRef.nativeElement,
            filteredSuggestionStream: this.suggestionDataSource.$filteredSuggestions,
            filterStream: this.filterStream,
            lightLayout: this.lightLayout,
            defaultTextName: this.defaultTextName,
            cpGroupTexts: this.suggestionDataSource.$textsGroup
        };
        this.suggestionSelectorOverlayRef = this.suggestionSelectorOverlayComponentService.open(
            this.suggestionSelectorOverlayConfig,
        );

        this.initOnListenEvent();
    }

    private initOnListenEvent() {
        /* listens for events coming from the overlay */
        this.selectedSuggestionSub = this.suggestionSelectorOverlayRef.selectedSuggestionEventEmitter
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((extendedSuggestion) => {
                this.onSelectorSuggestionSelected(extendedSuggestion);
            });
        this.preSelectedSuggestionSub = this.suggestionSelectorOverlayRef.preSelectedSuggestionEmitter
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((suggestion) => {
                this.preSelectedSuggestion = suggestion;
            });
        this.updateSuggestionsSub = this.suggestionSelectorOverlayRef.updatedSuggestionsEventEmitter
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((sugg) => {
                this.onUpdateSuggestions(sugg);
            })
        this.overlayStatusEmitter = this.suggestionSelectorOverlayRef.overlayStatusEmitter.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
            if (!val) {
                this.showSuggestions = false;
            }
        });
    }

    onUpdateSuggestions(sugg: SaveExtendedSuggestionEvent) {
        this.updateSuggestion.emit(sugg);
    }

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
        for (const propName of Object.keys(changes)) {
            if (propName === 'autoCompleteEnabled') {
                if (!this.autoCompleteEnabled) {
                    this.filterStream.next(noMatchingFilter);
                }
            }
            if (propName === 'suggestionList' && this.suggestionDataSource) {
                this.dsSuggestionStream.next(changes[propName].currentValue);                
            }
            if (propName === 'customCommandConfiguration' && !changes[propName].firstChange) {
                throw new Error('customCommandConfiguration can not be changed after first initialization');
            }
            if (propName === 'cpTextEditorSuggestionText' && this.suggestionDataSource) {
                this.textsGroupStream.next(changes[propName].currentValue);
            }
        }
    }

    public focus(): void {
        this.textEditorComponent.focus();
    }

    public clear(): void {
        this.textEditorComponent.clear();
    }

    public appendText(text: string) {
        this.textEditorComponent.appendText(text);
    }

    public replaceText(text: string) {
        this.textEditorComponent.replaceText(text);
    }

    public onTextEditorCommandRequested(event: MenuEvent) {
        this.commandRequested.emit(event);
    }

    public onTextEditorContentChange(event: ContentChangedEvent, autoCompleteEnabled: boolean) {
        this.contentChanged.emit(event);
        this.updateSuggestionFilterIfNeeded(event, autoCompleteEnabled);
    }

    public onTextEditorRequestSuggestion() {
        /* l'utente ha esplicitamente richiesto la lista di suggerimenti completa cliccando sulla lampadina */
        this.showSuggestions = true;
        this.filterStream.next(allMatchingFilter);
        this.textEditorComponent.focus();
    }

    public onTextSuggestion() {
        this.textEditorComponent.focus();
    }

    onVisibilityChange(visible: boolean) {
        this.visible = visible;

        this.visibilityNotify.emit(visible);
    }

    onIsEmptyChange(isEmpty: boolean) {
        this.isEmpty.emit(isEmpty);
    }

    onAddNewInEmptySuggestions(areEmpty: boolean) {
        if (areEmpty) {
            this.suggestionSelectorOverlayConfig = {
                connectedComponentRef: this.textEditorElementRef.nativeElement,
                filteredSuggestionStream: this.suggestionDataSource.$filteredSuggestions,
                filterStream: this.filterStream,
                lightLayout: this.lightLayout,
                defaultTextName: this.defaultTextName,
                cpGroupTexts: this.suggestionDataSource.$textsGroup
            };
            this.suggestionSelectorOverlayRef = this.suggestionSelectorOverlayComponentService.open(
                this.suggestionSelectorOverlayConfig,
            );
            this.initOnListenEvent();
            this.textEditorComponent.focus();
        }
    }

    onStartSpeechReconition(text: Array<string>) {
        this.startSpeechReconition.emit(text);
    }

    getActionFromShortcut(shortcut: string) {
        return Object.keys(this.shortcutList).find((key) => this.shortcutList[key] === shortcut);
    }

    public onTextEditorShortcut(event: ShortcutEvent) {
        if (event.shortcut.startsWith('F')) {
            // F1, ... , F12 shortcuts: insert suggestion
            this.onTextEditorInsertSuggestionShortcut(event.shortcut);
        } else {
            const action = this.getActionFromShortcut(event.shortcut);
            switch (action) {
                case 'select':
                    if (this.suggestionSelectorOverlayRef && !this.suggestionSelectorOverlayRef.isClosed) {
                        this.onTextEditorInsertPreSelectedSuggestion();
                    } else {
                        this.textEditorComponent.splitParagraph();
                    }
                    break;
                case 'refresh':
                    this.clear();
                    break;
                case 'suggestion-open':
                    this.onTextEditorRequestSuggestion();
                    break;
                case 'apply-normality':
                    this.textEditorComponent.applyNormality();
                    break;
                case 'bold':
                    this.textEditorComponent.setTextFormatAttributes('paragraphFontWeight', TextFormats.bold);
                    break;
                case 'italic':
                    this.textEditorComponent.setTextFormatAttributes('paragraphFontStyle', TextFormats.italic);
                    break;
                case 'underline':
                    this.textEditorComponent.setTextFormatAttributes('paragraphTextDecoration', TextFormats.underline);
                    break;
                case 'align-left':
                    this.textEditorComponent.setParagraphAttributes('paragraphAlign', 'left');
                    break;
                case 'align-center':
                    this.textEditorComponent.setParagraphAttributes('paragraphAlign', 'center');
                    break;
                case 'align-right':
                    this.textEditorComponent.setParagraphAttributes('paragraphAlign', 'right');
                    break;
                case 'align-justify':
                    this.textEditorComponent.setParagraphAttributes('paragraphAlign', 'justify');
                    break;
                case 'ordered-list':
                    this.textEditorComponent.onListClick([ListFormats.ordered]);
                    break;
                case 'unordered-list':
                    this.textEditorComponent.onListClick([ListFormats.unordered]);
                    break;
            }
        }
    }

    private updateSuggestionFilterIfNeeded(event: ContentChangedEvent, autoCompleteEnabled: boolean) {
        let filterText: string = typeof event.currentParagraphText !== 'undefined' ? event.currentParagraphText : '';
        if (this.autoCompleteTriggers) {
            this.autoCompleteTriggers.forEach((triggerStr: string) => {
                if (filterText.lastIndexOf(triggerStr) >= 0) {
                    // prima era >0. Con >=0 funziona anche se il primo carattere è un segno di punteggiatura
                    filterText = filterText.substring(filterText.lastIndexOf(triggerStr) + 1, filterText.length);
                }
            });
        }
        this.filteredText = filterText;
        filterText = filterText.trim();
        if (autoCompleteEnabled) {
            const filter = new SuggestionFilter();
            filter.code = filterText;
            filter.text = filterText;
            this.showSuggestions = true;
            this.filterStream.next(filter);
        }
    }

    // Tasto invio sulla lista dei suggerimenti
    private onTextEditorInsertPreSelectedSuggestion() {
        if (this.preSelectedSuggestion && this.suggestionSelectorOverlayRef) {
            const extendedSuggestion: ExtendedSuggestion = { suggestion: this.preSelectedSuggestion, policy: true };
            this.textEditorComponent.insertWithinCurrentParagraph(
                this.filteredText,
                this.getSentenceForTypeText(extendedSuggestion),
                extendedSuggestion.suggestion.newLineAfter,
            );
            this.selectSuggestionForDefaultTextGroup(extendedSuggestion);
        }
    }

    // Tasti shortcut F1.. Fn dei suggerimenti
    private onTextEditorInsertSuggestionShortcut(shortcutIndex: string) {
        const index = shortcutIndex.substring(1);
        const extendedSuggestion = {
            suggestion: this.suggestionDataSource.getSuggestion(parseInt(index, 10) - 1),
            policy: true,
        };
        if (extendedSuggestion.suggestion) {
            this.textEditorComponent.insertWithinCurrentParagraph(
                '',
                this.getSentenceForTypeText(extendedSuggestion),
                extendedSuggestion.suggestion.newLineAfter,
            );
            this.selectSuggestionForDefaultTextGroup(extendedSuggestion);
        }
    }

    // Click sulla lista dei suggerimenti
    public onSelectorSuggestionSelected(event: ExtendedSuggestion) {
        if (event.policy) {
            // append (default)
            this.textEditorComponent.insertWithinCurrentParagraph(
                this.isAutoComplete() ? this.filteredText : '',
                this.getSentenceForTypeText(event),
                event.suggestion.newLineAfter,
            );
        } else {
            // substitute
            this.textEditorComponent.replaceContent(this.getSentenceForTypeText(event));
        }
        this.textEditorComponent.focus();
        this.selectSuggestionForDefaultTextGroup(event);
    }

    private selectSuggestionForDefaultTextGroup(extendedSuggestion: ExtendedSuggestion) {
        if (!this.lightLayout) {
            this.selectedSuggestion.emit(extendedSuggestion);
        }
    }

    private isAutoComplete() {
        const filter = this.suggestionDataSource.getFilter();
        return filter.text;
    }

    private getSentenceForTypeText(extendedSuggestion: ExtendedSuggestion): string {
        if (!this.defaultTextName) {
            return '';
        }
        return extendedSuggestion.suggestion.Sentence[this.defaultTextName];
    }

    /* control value accessor */
    writeValue(obj: any): void {
        this.value = obj;
    }

    registerOnChange(fn: any): void {
        this.contentChanged
            .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
            .subscribe((e: ContentChangedEvent) => fn(e.innerHTML));
    }

    registerOnTouched(fn: any): void {
        this.textEditorComponent.registerOnTouched(fn);
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        if (this.suggestionDataSource) {
            this.suggestionDataSource.close();
        }
    }

    public onKeyDown(e) {
        if (this.suggestionSelectorOverlayRef && !this.suggestionSelectorOverlayRef.isClosed) {
            if (e.code === 'ArrowUp' || e.key === 'Up' || e.code === 'ArrowDown' || e.key === 'Down') {
                e.preventDefault();
                e.cancelBubble = true;
            }
        }
    }

    public onSelectionChange(_: SelectEvent) {
        // vuota
    }
}
